import axios from "axios";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FaCopy, FaEye, FaEyeSlash, FaInfoCircle, FaKey, FaPlug, FaShopify, FaSyncAlt } from "react-icons/fa";
import StoreImporter, { Alert, Spinner } from "../StoreImport/StoreImporter";
import { FormSkeleton } from "../WooCommerce/WooCommerceStoreCloner";

const SOURCE = {
  name: "Shopify",
  Icon: FaShopify,
  api: "/api/admin/shopify-import",
  groups: "collections",
  subtitle: "Copy collections, products, variants, images, and SEO from a Shopify store into this catalog.",
  connectHint: "Store domain and app keys",
  colors: { accent: "#008060", dark: "#006e52", light: "#36b38f", soft: "rgba(0, 128, 96, 0.1)", ring: "rgba(0, 128, 96, 0.3)" },
};

const LABELS = {
  disconnected: "Not connected",
  connected: "Connected",
  authorized: "Authorized",
  needs_authorization: "Needs store owner approval",
  insufficient_permissions: "Missing permissions",
  error: "Connection failed",
};

// The import needs product and stock access; write access includes read.
function ScopeChips({ scopes }) {
  const has = (area) => scopes.includes(`read_${area}`) || scopes.includes(`write_${area}`);
  const needed = [
    { area: "products", label: "Products" },
    { area: "inventory", label: "Stock counts" },
  ];
  return (
    <>
      {needed.map(({ area, label }) => (
        <span key={area} className="si-chip" style={has(area) ? undefined : { color: "#b45309", borderColor: "rgba(245, 158, 11, 0.4)" }}>
          {has(area) ? "✓" : "✗"} <strong>{label}</strong> {has(area) ? "allowed" : `need read_${area}`}
        </span>
      ))}
      <span className="si-chip" title={scopes.join(", ")}>{scopes.length} permissions granted</span>
    </>
  );
}

export default function ShopifyStoreCloner() {
  const location = useLocation();
  const history = useHistory();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connection, setConnection] = useState(null);
  const [shopDomain, setShopDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [working, setWorking] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);

  const applyStatus = (data) => {
    setConnection(data);
    if (data.shop_domain) setShopDomain(data.shop_domain);
    if (data.client_id) setClientId(data.client_id);
    if (["needs_authorization", "insufficient_permissions", "error"].includes(data.auth_status)) {
      setError(data.message || "");
    }
  };

  useEffect(() => {
    document.title = "Shopify import — Admin";
    const params = new URLSearchParams(location.search);
    if (params.get("shopify") === "connected") setNotice("The store returned here. The authorization was saved.");
    if (params.get("shopify") === "error") setError("Authorization did not finish. Start it again from this page.");
    if (params.has("shopify")) history.replace({ pathname: location.pathname, search: "" });
    axios.get(`${SOURCE.api}/status`).then((response) => {
      applyStatus(response.data || {});
    }).catch(() => {
      setError("The store connection could not be loaded.");
    }).finally(() => setLoadingStatus(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const credentials = () => {
    const payload = { shop_domain: shopDomain.trim(), client_id: clientId.trim() };
    if (clientSecret.trim()) payload.client_secret = clientSecret.trim();
    return payload;
  };

  const testConnection = async () => {
    setError("");
    setNotice("");
    setWorking("test");
    try {
      const response = await axios.post(`${SOURCE.api}/connect`, credentials());
      applyStatus(response.data || {});
      setClientSecret("");
      setShowSecret(false);
      if (["connected", "authorized"].includes(response.data.auth_status)) {
        setNotice(response.data.message || "Connected.");
        setError("");
      }
    } catch (err) {
      const data = err.response?.data || {};
      if (data.auth_status) applyStatus(data);
      setError(data.message || "The store rejected the connection.");
    } finally {
      setWorking("");
    }
  };

  const authorize = async () => {
    setError("");
    setWorking("authorize");
    try {
      if (shopDomain.trim() && clientId.trim()) {
        await axios.post(`${SOURCE.api}/connect`, credentials()).catch((err) => {
          if (err.response?.data?.auth_status !== "needs_authorization") throw err;
        });
        setClientSecret("");
      }
      const response = await axios.post(`${SOURCE.api}/authorize`);
      window.location.assign(response.data.authorize_url);
    } catch (err) {
      setError(err.response?.data?.message || "The authorization link could not be created.");
      setWorking("");
    }
  };

  const redirectUri = connection?.redirect_uri || "";
  const copyRedirect = () => {
    if (!redirectUri || !navigator.clipboard) return;
    navigator.clipboard.writeText(redirectUri).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const status = connection?.auth_status || "disconnected";
  const ready = status === "connected" || status === "authorized";
  const label = ready && connection?.shop_name ? `${LABELS[status]} · ${connection.shop_name}` : (LABELS[status] || "Not connected");
  const pillStatus = ready ? "connected" : (["error", "insufficient_permissions", "needs_authorization"].includes(status) ? "error" : status);
  const connecting = working !== "";

  return (
    <StoreImporter
      source={SOURCE}
      connection={{ status: loadingStatus ? "loading" : pillStatus, ready, label }}
      connecting={connecting}
    >
      <div className="si-card">
        <div className="si-card-head">
          <h5>Source store</h5>
          {connection?.shop_domain && !loadingStatus ? <span className="si-badge">{connection.shop_domain}</span> : null}
        </div>
        <div className="si-card-body">
          {loadingStatus ? (
            <FormSkeleton />
          ) : (
            <>
              <div className="row g-3">
                <div className="col-lg-4">
                  <label className="form-label" htmlFor="shopify-domain">Store domain</label>
                  <input id="shopify-domain" className="form-control" placeholder="your-store.myshopify.com" value={shopDomain}
                    onChange={(event) => setShopDomain(event.target.value)} disabled={connecting} />
                </div>
                <div className="col-lg-4">
                  <label className="form-label" htmlFor="shopify-client">Client ID</label>
                  <input id="shopify-client" className="form-control" placeholder="From the Dev Dashboard" value={clientId}
                    onChange={(event) => setClientId(event.target.value)} autoComplete="off" disabled={connecting} />
                </div>
                <div className="col-lg-4">
                  <label className="form-label" htmlFor="shopify-secret">Client secret</label>
                  <div className="si-input-wrap">
                    <input id="shopify-secret" type={showSecret ? "text" : "password"} className="form-control"
                      placeholder={connection?.has_secret ? "Saved on the server" : "From the Dev Dashboard"}
                      value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} autoComplete="new-password" disabled={connecting} />
                    <button type="button" className="si-eye" onClick={() => setShowSecret(!showSecret)} aria-label={showSecret ? "Hide secret" : "Show secret"}>
                      {showSecret ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="si-help">
                <FaInfoCircle />
                <div>
                  Create an app for the source store with the Admin API scopes <strong>read_products</strong> and <strong>read_inventory</strong>, release that version, and install it on the store. Paste its Client ID and Client secret here; they stay on this server.
                  A store in your own organization connects with <strong>Test Connection</strong>. A store owned by someone else needs <strong>Authorize store</strong>, and that store owner must approve the app.
                </div>
              </div>

              <div className="si-actions">
                <button type="button" className={`si-btn ${ready ? "" : "si-btn-dark"}`} onClick={testConnection} disabled={connecting}>
                  {working === "test" ? <Spinner /> : (ready ? <FaSyncAlt /> : <FaPlug />)}
                  {working === "test" ? "Connecting…" : (ready ? "Reconnect" : "Test Connection")}
                </button>
                <button type="button" className="si-btn" onClick={authorize} disabled={connecting}>
                  {working === "authorize" ? <Spinner /> : <FaKey />}
                  {working === "authorize" ? "Opening Shopify…" : "Authorize store"}
                </button>
              </div>

              <div className="si-meta-line">
                {redirectUri ? (
                  <span className="si-chip">
                    Redirect URL <strong className="si-code">{redirectUri}</strong>
                    <button type="button" className="si-eye position-static p-0 ms-1" style={{ transform: "none" }} onClick={copyRedirect} aria-label="Copy redirect URL" title="Copy">
                      <FaCopy size={11} />
                    </button>
                    {copied ? <span className="text-success">Copied</span> : null}
                  </span>
                ) : null}
                {connection?.scopes && connection.scopes.length ? (
                  <ScopeChips scopes={connection.scopes} />
                ) : null}
              </div>
            </>
          )}
          {notice ? <Alert tone="success" onClose={() => setNotice("")}>{notice}</Alert> : null}
          {error ? <Alert tone="error" onClose={() => setError("")}>{error}</Alert> : null}
        </div>
      </div>
    </StoreImporter>
  );
}
